import { useState } from 'react';
import { assigneeLabel, pickerOptions, type Assignee } from '../../lib/assignees';
import { CloseIcon } from '../icons';

/**
 * Pick several people. Chips for who is on, a suggestion list to add more.
 *
 * Takes its chip markup from TagInput, but not its `<datalist>`. A datalist is
 * fine for free-form tags, where it only ever saves typing; it is not fine for
 * an entity picker, where it was the *only* way to name somebody. It matches
 * whole option text, so anything short of the full name -- "Nadhem" for "Nadhem
 * Alfardan" -- came back "Nobody matches that", and its popup is a native widget
 * that renders outside the page and cannot be styled, scrolled with the panel,
 * or trusted to appear. So the list here is ordinary markup: it filters on any
 * part of a name or email, arrow keys walk it, and Enter takes the highlighted
 * row.
 *
 * Two deliberate departures from TagInput. There is no commit on blur: a tag is
 * whatever you typed, but a half-typed name must never silently resolve to a
 * person. And already-chosen people are dropped from the options, so the list
 * shrinks as you go rather than offering duplicates the server would refuse.
 */
export function AssigneePicker({
  id,
  label,
  assignees,
  selectedIds,
  excludeIds = [],
  placeholder = 'Add someone…',
  compact = false,
  onChange,
}: {
  /** So the surrounding `<label>` points at the text box people actually type in. */
  id?: string;
  label: string;
  /** The whole roster, archived included. */
  assignees: Assignee[];
  selectedIds: string[];
  /** People this control must not offer -- the owner, who belongs to the other
   *  control. Not the same as `selectedIds`: these are not on the list either. */
  excludeIds?: string[];
  placeholder?: string;
  /** Same meaning as on TagInput and the ui fields: the panel's density. */
  compact?: boolean;
  onChange: (ids: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [missed, setMissed] = useState(false);

  const byId = new Map(assignees.map((assignee) => [assignee.id, assignee]));

  // Archived people stay visible while they are on the task -- removing them is
  // a decision, not something an edit elsewhere should do quietly -- but they
  // are not offered as new additions.
  const selectable = pickerOptions(assignees, selectedIds).filter(
    (assignee) =>
      !assignee.archived && !selectedIds.includes(assignee.id) && !excludeIds.includes(assignee.id),
  );

  // Substring rather than prefix: people search for the surname as often as the
  // first name, and the roster is small enough that a looser match never floods.
  const needle = draft.trim().toLowerCase();
  const matches = needle
    ? selectable.filter(
        (assignee) =>
          assignee.name.toLowerCase().includes(needle) ||
          (assignee.email?.toLowerCase().includes(needle) ?? false),
      )
    : selectable;

  // The highlight is stored as an index, so a list that shrinks under it has to
  // be clamped on the way out rather than tracked on every keystroke.
  const activeIndex = matches.length === 0 ? -1 : Math.min(active, matches.length - 1);

  function add(assignee: Assignee) {
    setDraft('');
    setActive(0);
    setMissed(false);
    setOpen(false);
    onChange([...selectedIds, assignee.id]);
  }

  function commit() {
    const picked = activeIndex >= 0 ? matches[activeIndex] : undefined;
    if (picked) {
      add(picked);
      return;
    }
    // Typing is not a failure; only a committed non-match is.
    setMissed(draft.trim().length > 0);
  }

  const listId = id ? `${id}-options` : undefined;
  const optionId = (index: number) => (listId ? `${listId}-${index}` : undefined);

  return (
    <div className="relative">
      <div
        className={`flex flex-wrap items-center gap-1.5 rounded-lg border border-stone-300 bg-white shadow-sm transition focus-within:border-honey-500 focus-within:ring-2 focus-within:ring-honey-500/30 ${
          compact ? 'px-1.5 py-1' : 'px-2 py-1.5'
        }`}
      >
        {selectedIds.map((entryId) => {
          const assignee = byId.get(entryId);
          // An id with no matching record means the roster has not loaded yet,
          // or the person was deleted in another tab. Showing the raw id would
          // be noise, so the chip waits.
          if (!assignee) return null;

          return (
            <span
              key={entryId}
              className={`inline-flex items-center gap-1 rounded-full py-0.5 pl-2 pr-1 text-xs font-medium ${
                assignee.archived ? 'bg-amber-50 text-amber-800' : 'bg-stone-100 text-stone-700'
              }`}
            >
              {assignee.name}
              {assignee.archived ? ' (archived)' : ''}
              <button
                type="button"
                onClick={() => onChange(selectedIds.filter((entry) => entry !== entryId))}
                aria-label={`Remove ${assignee.name}`}
                className="rounded-full px-1 text-stone-500 hover:bg-stone-200 hover:text-stone-900"
              >
                <CloseIcon className="h-3 w-3" />
              </button>
            </span>
          );
        })}

        <input
          id={id}
          value={draft}
          aria-label={label}
          placeholder={selectedIds.length === 0 ? placeholder : ''}
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          // The browser's own history dropdown would sit on top of this one.
          autoComplete="off"
          onFocus={() => setOpen(true)}
          // Clicking an option fires mousedown first and cancels the blur, so
          // by the time this runs the choice has already been made.
          onBlur={() => setOpen(false)}
          onChange={(event) => {
            setDraft(event.target.value);
            setActive(0);
            setMissed(false);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActive((index) => Math.min(index + 1, Math.max(matches.length - 1, 0)));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              // Enter would otherwise submit the surrounding form.
              event.preventDefault();
              commit();
            } else if (event.key === 'Escape' && open) {
              // Marked handled so the one keypress closes the list without also
              // closing the panel the list is sitting in.
              event.preventDefault();
              setOpen(false);
            } else if (event.key === 'Backspace' && draft === '' && selectedIds.length > 0) {
              onChange(selectedIds.slice(0, -1));
            }
          }}
          className={`min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-0 ${
            compact ? 'text-dense' : 'text-sm'
          }`}
        />
      </div>

      {open && matches.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-10 mt-1.5 max-h-56 w-full overflow-y-auto rounded-xl border border-stone-200 bg-white py-1 shadow-lg"
        >
          {matches.map((assignee, index) => (
            <li
              key={assignee.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              // mousedown, not click: click lands after blur has already closed
              // the list, so there would be nothing left to click.
              onMouseDown={(event) => {
                event.preventDefault();
                add(assignee);
              }}
              onMouseEnter={() => setActive(index)}
              className={`cursor-pointer px-3 py-1 text-dense ${
                index === activeIndex ? 'bg-honey-50 text-stone-900' : 'text-stone-700'
              }`}
            >
              {/* The same label the old datalist used: a name on its own where
                  that is unambiguous, qualified when two people share one. */}
              {assigneeLabel(assignee, assignees)}
              {assignee.role ? (
                <span className="ml-2 text-xs text-stone-500">{assignee.role}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {missed ? <p className="mt-1 text-dense text-stone-500">Nobody matches that.</p> : null}
    </div>
  );
}
